/**
 * File: planBilling.ts
 * Path: packages/config/src/retainers/planBilling.ts
 * Description: Care plans bill themselves. Each month's invoice is drafted
 * 7 days before its due date into the "Ready to send" list; one tap sends
 * it, or, on a plan switched to send automatically, it goes out by itself
 * unless something holds it for Liam:
 *
 *   renewal      month 13: the minimum term is over, it moves to month-to-month
 *   priceChange  the amount differs from the plan's last invoice
 *   notice       the plan has a cancellation notice (an end date, or cancelled)
 *   late         it was drafted after its due date (the timer missed it, or the
 *                plan was paused): it may not be owed
 *
 * A held month is still drafted; only the sending waits.
 *
 * When a month is due: a contract's retainer follows the contract (a date
 * set by hand on the payment, else its monthly due day counted from the
 * day the client signed, as Money owed and Still to bill show it); a plan
 * added by hand falls due on its start date's day each month.
 *
 * Each month has a fixed draft id (invoices/careplan-<plan>-M03), so the
 * daily timer can run any number of times and drafts a month once; a draft
 * thrown away keeps its id, so that month is never drafted again.
 *
 * What a plan has been paid and still owes is read from the books
 * (invoiceLedger.ts): its own invoices, its contract's invoices (linked
 * Zoho ones included), and, for a plan added by hand, invoices that name no
 * plan or contract, to the same client for exactly the monthly fee (marked
 * as matched that way, so Liam can check them).
 *
 * Pure, so the timer, the admin and the tests agree.
 */

import { randToCents } from "../accounting/money";
import { contractSignedOn, type PaymentDueDate } from "../contracts/contractDue";
import { monthlyDueDate, toMonthlyDueDay } from "../contracts/contractDueDay";
import { contractPayments } from "../contracts/contractSchedule";
import type { Contract, ContractBankDetails } from "../contracts/contractTypes";
import type { InvoiceClient, InvoiceDraft } from "../invoices/invoiceDraft";
import { addDays, sameCustomer, type InvoiceLedger, type LedgerInvoice, type LedgerLink } from "../invoices/invoiceLedger";
import type { CarePlanMonth, PlanHoldReason, SavedInvoice } from "../invoices/invoiceRecord";
import { formatCarePlanReference, formatPaymentPart, formatPaymentReference, monthPart } from "../references/references";
import { renewalDate, type Retainer } from "./retainers";

/** A month is drafted this many days before it is due. */
export const PLAN_DRAFT_DAYS_AHEAD = 7;
/** A month whose draft was missed is still drafted (and held) this many days after its due date; older ones are left alone. */
export const PLAN_CATCH_UP_DAYS = 14;
/**
 * Months due before this day are history: billed in Zoho or by hand before
 * care plans billed themselves, so they are never drafted.
 */
export const PLAN_BILLING_STARTS = "2026-10-18";

export const PLAN_HOLD_LABELS: Record<PlanHoldReason, string> = {
  renewal: "Month 13: the minimum term is over and the plan moves to month-to-month.",
  priceChange: "The amount is different from the plan's last invoice.",
  notice: "The plan has a cancellation notice.",
  late: "Drafted after its due date: check it is still owed.",
};

/** The words for why a draft is held, in the order given. */
export const planHoldText = (holds: readonly PlanHoldReason[]): string[] => holds.map((h) => PLAN_HOLD_LABELS[h]);

/** invoices/<id> for month `month` of a plan: the same on every run. */
export const planDraftId = (planId: string, month: number): string => `careplan-${planId}-${formatPaymentPart(monthPart(month))}`;

/** A plan with what it bills against. */
export interface PlanTerms {
  plan: Retainer;
  /** The signed contract behind a contract's retainer; null for a plan added by hand. */
  contract: Contract | null;
  /** Due dates set by hand on the contract's payments (contracts/<id>/dueDates). */
  dueDates?: readonly PaymentDueDate[];
}

const termMonths = (contract: Contract): number => Math.max(1, Math.round(contract.content.minimumTermMonths));

/** A retainer paid for its whole minimum term up front: those months are billed by its deposit. */
const prepaidMonths = (contract: Contract | null): number =>
  contract && contract.content.template === "retainer" && contract.content.retainerBilling === "once_off" ? termMonths(contract) : 0;

/** What the client pays month `month` with; "" when none can be made, and the invoice then uses its own number. */
export const planMonthReference = (terms: PlanTerms, month: number): string => {
  try {
    return terms.contract
      ? formatPaymentReference(terms.contract.reference, monthPart(month))
      : formatCarePlanReference(terms.plan.id, month);
  } catch {
    return "";
  }
};

const workedOutDueOn = (terms: PlanTerms, month: number): string =>
  terms.contract
    ? monthlyDueDate(contractSignedOn(terms.contract) ?? terms.plan.startDate, month, toMonthlyDueDay(terms.contract.content.monthlyDueDay))
    : monthlyDueDate(terms.plan.startDate, month, "signing");

/** The day month `month` of a plan is due (YYYY-MM-DD). */
export const planMonthDueOn = (terms: PlanTerms, month: number): string => {
  const reference = terms.contract ? planMonthReference(terms, month) : "";
  const set = reference ? terms.dueDates?.find((d) => d.reference === reference)?.dueOn : undefined;
  return set ?? workedOutDueOn(terms, month);
};

/** Whole cents for month `month`: the contract's own figure while its term runs, else the plan's monthly fee. */
export const planMonthCents = (terms: PlanTerms, month: number): number => {
  if (terms.contract && month <= termMonths(terms.contract)) {
    const payment = contractPayments(terms.contract.content).find((p) => p.part.kind === "month" && p.part.n === month);
    if (payment) return randToCents(payment.amount);
  }
  return terms.plan.monthlyCents;
};

const monthName = (day: string): string =>
  new Intl.DateTimeFormat("en-ZA", { month: "long", year: "numeric", timeZone: "UTC" }).format(Date.parse(`${day}T00:00:00Z`));

/** The invoice line: "Care plan, month 3 (November 2026)", with the contract on a contract's retainer. */
export const planMonthLine = (terms: PlanTerms, month: number): string => {
  const base = `${terms.plan.kind}, month ${month} (${monthName(planMonthDueOn(terms, month))})`;
  return terms.contract?.reference ? `${base}, contract ${terms.contract.reference}` : base;
};

/** The first month due on or after `day`. */
const firstMonthFrom = (terms: PlanTerms, month: number, day: string): boolean =>
  planMonthDueOn(terms, month) >= day && (month === 1 || planMonthDueOn(terms, month - 1) < day);

/** Why month `month` waits for Liam; empty when it may go out by itself. */
export const planMonthHolds = (
  terms: PlanTerms,
  month: number,
  input: { today: string; lastInvoicedCents: number | null },
): PlanHoldReason[] => {
  const { plan, contract } = terms;
  const holds: PlanHoldReason[] = [];
  const renewal = contract
    ? month === termMonths(contract) + 1 || (plan.renewsOn !== null && firstMonthFrom(terms, month, plan.renewsOn))
    : firstMonthFrom(terms, month, renewalDate(plan));
  if (renewal) holds.push("renewal");
  if (input.lastInvoicedCents !== null && planMonthCents(terms, month) !== input.lastInvoicedCents) holds.push("priceChange");
  if (plan.endDate !== null || plan.status === "cancelled") holds.push("notice");
  if (planMonthDueOn(terms, month) < input.today) holds.push("late");
  return holds;
};

/** How far ahead of the draft window months are looked at, so a due date set later by hand is still found. */
const LOOK_AHEAD_DAYS = 400;

/**
 * The months of a plan that need a draft as of `today`: due within the next
 * 7 days (or missed in the last 14), not before billing started, within the
 * plan's end date, not billed by its up-front deposit, and with no invoice
 * yet (no draft under its id, whatever became of it, and no invoice for its
 * payment reference). A paused plan, a completed one (it ran its term and
 * is paid up), and a cancelled one with no end date, need none.
 */
export const planMonthsToDraft = (
  terms: PlanTerms,
  input: {
    today: string;
    existingIds: ReadonlySet<string>;
    invoicedReferences: ReadonlySet<string>;
    billingStarts?: string;
  },
): number[] => {
  const { plan, contract } = terms;
  if (plan.status === "paused" || plan.status === "completed" || (plan.status === "cancelled" && plan.endDate === null)) return [];
  const latest = addDays(input.today, PLAN_DRAFT_DAYS_AHEAD);
  const starts = input.billingStarts ?? PLAN_BILLING_STARTS;
  const catchUp = addDays(input.today, -PLAN_CATCH_UP_DAYS);
  const earliest = starts > catchUp ? starts : catchUp;
  const horizon = addDays(latest, LOOK_AHEAD_DAYS);
  const months: number[] = [];
  for (let month = prepaidMonths(contract) + 1; workedOutDueOn(terms, month) <= horizon; month += 1) {
    const dueOn = planMonthDueOn(terms, month);
    if (dueOn > latest || dueOn < earliest) continue;
    if (plan.endDate !== null && dueOn > plan.endDate) continue;
    if (input.existingIds.has(planDraftId(plan.id, month))) continue;
    const reference = planMonthReference(terms, month);
    if (reference && input.invoicedReferences.has(reference)) continue;
    months.push(month);
  }
  return months;
};

/** One month's draft, ready to save as invoices/<id>. */
export interface PlanDraft {
  id: string;
  retainerId: string;
  contractId: string | null;
  clientId: string | null;
  carePlan: CarePlanMonth;
  draft: InvoiceDraft;
  /** The plan sends by itself and nothing holds this month: issue it now. */
  sendNow: boolean;
}

export interface PlanDraftInput extends PlanTerms {
  today: string;
  /** Every invoices/<id> that exists, any status. */
  existingIds: ReadonlySet<string>;
  /** Payment references an invoice already covers (invoicedReferences). */
  invoicedReferences: ReadonlySet<string>;
  /** The total of the plan's last invoice (planMoney), for the price-change hold; null when it has none. */
  lastInvoicedCents: number | null;
  /** Who the invoice is for; a contract's retainer uses the contract's client. */
  client: InvoiceClient;
  bank: ContractBankDetails;
  billingStarts?: string;
}

/**
 * The drafts a plan needs today. Dated today (it is dated again the day it
 * is issued) and due on the month's due date, never before today.
 */
export const planDrafts = (input: PlanDraftInput): PlanDraft[] => {
  const { plan, contract } = input;
  const c = contract?.content.client;
  const client: InvoiceClient = c ? { name: c.name, address: c.address, email: c.email, registrationNo: c.registrationNo } : input.client;
  return planMonthsToDraft(input, input).map((month) => {
    const dueOn = planMonthDueOn(input, month);
    const holds = planMonthHolds(input, month, input);
    return {
      id: planDraftId(plan.id, month),
      retainerId: plan.id,
      contractId: contract?.id ?? null,
      clientId: contract?.clientId ?? plan.clientId,
      carePlan: { month, dueOn, holds },
      draft: {
        number: "",
        issuedOn: input.today,
        dueOn: dueOn < input.today ? input.today : dueOn,
        client,
        contractReference: contract?.reference ?? "",
        paymentReference: planMonthReference(input, month),
        lines: [{ description: planMonthLine(input, month), cents: planMonthCents(input, month) }],
        notes: "",
        bank: input.bank,
      },
      sendNow: plan.autoSend === true && holds.length === 0,
    };
  });
};

/** Payment references an invoice already covers: saved and not void or thrown away, or a linked Zoho invoice. */
export const invoicedReferences = (saved: readonly SavedInvoice[], links: readonly LedgerLink[]): Set<string> =>
  new Set([
    ...saved.filter((s) => s.status !== "void" && s.status !== "discarded" && s.draft.paymentReference).map((s) => s.draft.paymentReference),
    ...links.map((l) => l.reference),
  ]);

/** What one plan has been billed, paid and still owes, from the books. */
export interface PlanMoney {
  retainerId: string;
  /** Its issued invoices, newest first (drafts and void ones left out). */
  invoices: LedgerInvoice[];
  /** Its drafts: the plan's part of the Ready to send list. */
  drafts: LedgerInvoice[];
  invoicedCents: number;
  paidCents: number;
  creditedCents: number;
  /** Still owed on its invoices in the books. */
  owedCents: number;
  /** The part of that past its due date. */
  overdueCents: number;
  lastInvoice: LedgerInvoice | null;
  /** Invoices counted because they went to the same client for exactly the monthly fee, not because they name the plan: shown so Liam can check. */
  inferred: string[];
}

const byNewest = (a: LedgerInvoice, b: LedgerInvoice) => b.issuedOn.localeCompare(a.issuedOn) || b.number.localeCompare(a.number);

/** An invoice that names no plan or contract (Zoho's, or one made by hand): counted for the one hand-added plan it fits, if exactly one fits. */
const inferredPlan = (plans: readonly Retainer[], row: LedgerInvoice): Retainer | null => {
  const fits = plans.filter(
    (p) =>
      p.contractId === null &&
      p.monthlyCents > 0 &&
      row.totalCents === p.monthlyCents &&
      sameCustomer({ clientId: p.clientId, party: p.clientName }, row) &&
      row.issuedOn >= addDays(p.startDate, -31) &&
      (p.endDate === null || row.issuedOn <= p.endDate),
  );
  return fits.length === 1 ? fits[0] : null;
};

/**
 * Every plan's money, by plan id. An invoice belongs to the plan it was
 * drafted for, else to the plan of its contract (linked Zoho invoices carry
 * their contract), else, for a plan added by hand, to the one plan whose
 * client it went to for exactly the monthly fee.
 */
export const planMoney = (plans: readonly Retainer[], ledger: Pick<InvoiceLedger, "invoices">): Map<string, PlanMoney> => {
  const result = new Map<string, PlanMoney>(
    plans.map((p) => [
      p.id,
      { retainerId: p.id, invoices: [], drafts: [], invoicedCents: 0, paidCents: 0, creditedCents: 0, owedCents: 0, overdueCents: 0, lastInvoice: null, inferred: [] },
    ]),
  );
  const byId = new Map(plans.map((p) => [p.id, p]));
  const byContract = new Map(plans.filter((p) => p.contractId).map((p) => [p.contractId as string, p]));
  for (const row of ledger.invoices) {
    if (row.status === "void") continue;
    const named = (row.retainerId ? byId.get(row.retainerId) : undefined) ?? (row.contractId ? byContract.get(row.contractId) : undefined);
    const plan = named ?? (!row.retainerId && !row.contractId && row.status !== "draft" ? inferredPlan(plans, row) : null);
    const money = plan ? result.get(plan.id) : undefined;
    if (!money) continue;
    if (row.status === "draft") {
      money.drafts.push(row);
      continue;
    }
    if (!named) money.inferred.push(row.number);
    money.invoices.push(row);
    money.invoicedCents += row.totalCents;
    money.paidCents += row.paidCents;
    money.creditedCents += row.creditedCents;
    if (row.journalId !== null) {
      money.owedCents += Math.max(0, row.balanceCents);
      if (row.daysLate > 0) money.overdueCents += Math.max(0, row.balanceCents);
    }
  }
  for (const money of result.values()) {
    money.invoices.sort(byNewest);
    money.lastInvoice = money.invoices[0] ?? null;
  }
  return result;
};
